import { useEffect, useMemo, useRef, useState } from "react";
import "./CriarDesvioNegarModal.css";

const toUpperSafe = (v) => (typeof v === "string" ? v.toUpperCase() : v ?? "");
const ATIVO_OPTIONS = [
  { value: 1, label: "SIM" },
  { value: 0, label: "NAO" },
];

function CriarDesvioNegarDropdown({ label, name, value, options, onSelect, placeholder = "SELECIONE" }) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(220);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);
  const itemHeightRef = 44;

  const current = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) closeMenu();
    }
    document.addEventListener("mousedown", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const onWindowChange = () => {
      updatePlacement();
    };

    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    return () => {
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
    };
  }, [open, options.length]);

  function updatePlacement() {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const modal = wrapRef.current.closest(".dvncm-modal");
    const modalRect = modal?.getBoundingClientRect();

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const safePad = 12;

    const viewportBelow = Math.max(0, viewportH - rect.bottom - safePad);
    const viewportAbove = Math.max(0, rect.top - safePad);
    const modalBelow = modalRect ? Math.max(0, modalRect.bottom - rect.bottom - safePad) : Number.POSITIVE_INFINITY;
    const modalAbove = modalRect ? Math.max(0, rect.top - modalRect.top - safePad) : Number.POSITIVE_INFINITY;

    const spaceBelow = Math.max(0, Math.min(viewportBelow, modalBelow));
    const spaceAbove = Math.max(0, Math.min(viewportAbove, modalAbove));

    const target = Math.min(320, options.length * itemHeightRef + 12);
    const shouldOpenUp = spaceBelow < 190 && spaceAbove > spaceBelow;
    const available = shouldOpenUp ? spaceAbove : spaceBelow;
    const dynamicMax = Math.max(96, Math.min(target, available - 6));

    setOpenUp(shouldOpenUp);
    setMenuMaxHeight(dynamicMax);
  }

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setRenderMenu(true);
    requestAnimationFrame(() => {
      updatePlacement();
      setOpen(true);
    });
  };

  const closeMenu = () => {
    setOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setRenderMenu(false), 240);
  };

  const toggle = () => {
    if (open) closeMenu();
    else openMenu();
  };

  return (
    <div ref={wrapRef} className={`dvncm-field dvncm-field-select ${open ? "is-open" : ""} ${openUp ? "is-open-up" : ""}`}>
      <span className="dvncm-label">{label}</span>
      <div className="dvncm-pick-anchor">
        <button
          type="button"
          className="dvncm-pick-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={toggle}
        >
          <span className={`dvncm-pick-val ${current ? "" : "is-placeholder"}`}>
            {current ? current.label : placeholder}
          </span>
        </button>

        {renderMenu && (
          <div
            className={`dvncm-pick-menu ${open ? "is-open" : "is-closing"}`}
            role="listbox"
            aria-label={label}
            style={{ maxHeight: `${menuMaxHeight}px` }}
          >
            {options.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                className="dvncm-pick-opt"
                onClick={() => {
                  onSelect(name, opt.value);
                  closeMenu();
                }}
                role="option"
                aria-selected={String(opt.value) === String(value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CriarDesvioNegarModal({ open, initial, onClose, onSubmit }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      causador: initial?.causador ?? "",
      causa_primaria: initial?.causa_primaria ?? "",
      quando_utilizado: initial?.quando_utilizado ?? "",
      ativo: initial?.ativo === 0 || initial?.ativo === 1 ? Number(initial.ativo) : "",
    });
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter" && document.activeElement?.tagName?.toLowerCase() !== "textarea") {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, form]);

  if (!open || !form) return null;

  const canSave =
    !!String(form.causador || "").trim() &&
    !!String(form.causa_primaria || "").trim() &&
    (form.ativo === 0 || form.ativo === 1);

  function handleSubmit() {
    if (!canSave) return;
    onSubmit?.({
      causador: form.causador,
      causa_primaria: form.causa_primaria,
      quando_utilizado: form.quando_utilizado,
      ativo: Number(form.ativo),
    });
  }

  return (
    <div className="dvncm-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="dvncm-modal" role="dialog" aria-modal="true" aria-label="Criar Desvio Negar">
        <header className="dvncm-header">
          <h3 className="dvncm-title">Cadastrar</h3>
          <button type="button" className="dvncm-close" aria-label="Fechar" onClick={onClose}>
            x
          </button>
        </header>

        <div className="dvncm-body">
          <div className="dvncm-grid">
            <label className="dvncm-field">
              <span className="dvncm-label">Causador *</span>
              <input
                value={form.causador}
                onChange={(e) => setForm((prev) => ({ ...prev, causador: toUpperSafe(e.target.value) }))}
                placeholder="Ex.: EXECUCAO"
              />
            </label>

            <label className="dvncm-field">
              <span className="dvncm-label">Causa Primaria *</span>
              <input
                value={form.causa_primaria}
                onChange={(e) => setForm((prev) => ({ ...prev, causa_primaria: toUpperSafe(e.target.value) }))}
                placeholder="Ex.: RESTRICAO"
              />
            </label>

            <CriarDesvioNegarDropdown
              label="Ativo"
              name="ativo"
              value={form.ativo}
              options={ATIVO_OPTIONS}
              onSelect={(name, nextValue) => setForm((prev) => ({ ...prev, [name]: nextValue }))}
              placeholder="SELECIONE"
            />

            <label className="dvncm-field dvncm-field--full">
              <span className="dvncm-label">Quando Utilizado</span>
              <textarea
                rows={4}
                value={form.quando_utilizado}
                onChange={(e) => setForm((prev) => ({ ...prev, quando_utilizado: e.target.value }))}
                placeholder="Detalhe da justificativa..."
              />
            </label>
          </div>
        </div>

        <footer className="dvncm-footer">
          <button type="button" className="dvncm-btn dvncm-btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="dvncm-btn dvncm-btn--primary" onClick={handleSubmit} disabled={!canSave}>
            Salvar
          </button>
        </footer>
      </div>
    </div>
  );
}
